import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { userAPI, llmAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Navbar from '../components/Navbar';
import { User, KeyRound, Trash2, Plus, Shield } from 'lucide-react';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const [apiKeys, setApiKeys] = useState([]);
  const [providers, setProviders] = useState({});
  const [newProvider, setNewProvider] = useState('openai');
  const [newKey, setNewKey] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [keysRes, llmRes] = await Promise.all([
          userAPI.getApiKeys(),
          llmAPI.providers()
        ]);
        setApiKeys(keysRes.data.keys);
        setProviders(llmRes.data.providers);
      } catch {}
    };
    load();
  }, []);

  const handleAddKey = async (e) => {
    e.preventDefault();
    if (!newKey.trim()) return;
    setSaving(true);
    try {
      await userAPI.addApiKey({ provider: newProvider, api_key: newKey, label: newLabel });
      toast.success('API key saved');
      setNewKey('');
      setNewLabel('');
      const res = await userAPI.getApiKeys();
      setApiKeys(res.data.keys);
    } catch (err) {
      toast.error('Failed to save API key');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteKey = async (keyId) => {
    try {
      await userAPI.deleteApiKey(keyId);
      setApiKeys(prev => prev.filter(k => k.key_id !== keyId));
      toast.success('API key deleted');
    } catch {
      toast.error('Failed to delete key');
    }
  };

  return (
    <div className="min-h-screen bg-[#020202] grid-bg" data-testid="settings-page">
      <Navbar />
      <div className="pt-20 px-6 md:px-12 pb-12">
        <div className="max-w-3xl mx-auto">
          <div className="mb-10">
            <p className="font-mono text-xs text-[#FF7A3D] tracking-widest uppercase mb-2">SETTINGS</p>
            <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Configuration</h1>
          </div>

          {/* Profile */}
          <div className="card-cyber rounded-sm p-6 mb-4" data-testid="profile-section">
            <div className="flex items-center gap-3 mb-6">
              <User className="w-5 h-5 text-[#FF7A3D]" />
              <h2 className="font-heading text-lg font-bold text-white">PROFILE</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="font-mono text-xs text-[#999] tracking-widest">NAME</Label>
                <p className="font-body text-white mt-1">{user?.name || '-'}</p>
              </div>
              <div>
                <Label className="font-mono text-xs text-[#999] tracking-widest">EMAIL</Label>
                <p className="font-body text-white mt-1">{user?.email || '-'}</p>
              </div>
              <div>
                <Label className="font-mono text-xs text-[#999] tracking-widest">PLAN</Label>
                <Badge className="bg-[#FF7A3D]/10 text-[#FF7A3D] border-[#FF7A3D]/20 font-mono text-xs mt-1 uppercase">{user?.plan || 'free'}</Badge>
              </div>
              <div>
                <Label className="font-mono text-xs text-[#999] tracking-widest">CREDITS</Label>
                <p className="font-heading text-xl font-bold text-[#FF7A3D] mt-1">{(user?.credits || 0).toFixed(2)}</p>
              </div>
            </div>
          </div>

          {/* API Keys */}
          <div className="card-cyber rounded-sm p-6 mb-4" data-testid="api-keys-section">
            <div className="flex items-center gap-3 mb-6">
              <KeyRound className="w-5 h-5 text-[#00E5C1]" />
              <h2 className="font-heading text-lg font-bold text-white">LLM API KEYS</h2>
            </div>
            <p className="font-body text-sm text-[#999] mb-6">
              Add your own LLM API keys to use your preferred providers. Keys are encrypted and stored securely.
            </p>

            {/* Existing keys */}
            {apiKeys.length > 0 && (
              <div className="space-y-2 mb-6">
                {apiKeys.map(k => (
                  <div key={k.key_id} className="flex items-center justify-between p-3 bg-white/[0.02] border border-white/5 rounded-sm" data-testid={`api-key-${k.key_id}`}>
                    <div>
                      <p className="font-mono text-xs text-white">{k.label}</p>
                      <p className="font-mono text-[10px] text-[#aaa]">{k.provider.toUpperCase()} | {k.api_key}</p>
                    </div>
                    <button
                      onClick={() => handleDeleteKey(k.key_id)}
                      className="text-[#aaa] hover:text-[#ff003c] transition-colors"
                      data-testid={`delete-key-${k.key_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add new key */}
            <form onSubmit={handleAddKey} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="font-mono text-xs text-[#999] tracking-widest">PROVIDER</Label>
                  <Select value={newProvider} onValueChange={setNewProvider}>
                    <SelectTrigger className="bg-black border-white/10 font-mono text-xs mt-1" data-testid="new-key-provider">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10">
                      {Object.keys(providers).map(p => (
                        <SelectItem key={p} value={p} className="font-mono text-xs">{p.toUpperCase()}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-mono text-xs text-[#999] tracking-widest">LABEL</Label>
                  <Input
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    placeholder="My OpenAI Key"
                    className="bg-black border-white/10 font-mono text-xs mt-1 placeholder:text-[#555]"
                    data-testid="new-key-label"
                  />
                </div>
                <div>
                  <Label className="font-mono text-xs text-[#999] tracking-widest">API KEY</Label>
                  <Input
                    value={newKey}
                    onChange={(e) => setNewKey(e.target.value)}
                    placeholder="sk-..."
                    type="password"
                    className="bg-black border-white/10 font-mono text-xs mt-1 placeholder:text-[#555]"
                    data-testid="new-key-value"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={saving || !newKey.trim()}
                className="bg-[#00E5C1] text-black font-heading font-bold uppercase tracking-widest text-xs rounded-sm h-9 px-6 hover:bg-[#00E5C1]/90 disabled:opacity-30"
                data-testid="add-key-btn"
              >
                <Plus className="w-3 h-3 mr-2" /> ADD KEY
              </Button>
            </form>
          </div>

          {/* Logout */}
          <div className="card-cyber rounded-sm p-6" data-testid="logout-section">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading text-lg font-bold text-white">SESSION</h2>
                <p className="font-mono text-xs text-[#999]">End your current session</p>
              </div>
              <Button
                onClick={logout}
                variant="outline"
                className="border-[#ff003c]/30 text-[#ff003c] hover:bg-[#ff003c]/10 font-heading uppercase tracking-widest text-xs rounded-sm bg-transparent"
                data-testid="logout-btn"
              >
                TERMINATE SESSION
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
